/**
 * Pasos "¿qué día?" / "¿a qué hora?" del flujo de citas, compartidos entre
 * el flujo de CITA NUEVA (`pages/AppointmentsApp.tsx`) y el flujo de PEDIR
 * CAMBIO de una cita ya existente (`pages/AppointmentManage.tsx`).
 *
 * Extraídos tal cual (mismo JSX, mismas props) desde `AppointmentsApp.tsx`
 * para no duplicar el calendario mensual con días sin hueco deshabilitados
 * ni la rejilla de horas — ver `.claude/memorias/frontend.notas.md`
 * (18-sep-2026). Ambos consumidores siguen alimentándolos con
 * `GET /public/appointments/:slug/calendar` y `GET /public/appointments/:slug/slots`;
 * este fichero no hace fetch, solo pinta lo que le pasan por props.
 */

import { useMemo } from "react";
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Calendar as CalIcon,
  Users,
} from "lucide-react";
import {
  format,
  addDays,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isBefore,
  startOfDay,
  getDay,
} from "date-fns";
import { es } from "date-fns/locale";

export interface Slot {
  time: string;
  specialists: { id: string; name: string }[];
}

export function DateStep({
  month,
  onMonthChange,
  calendarDays,
  calendarLoading,
  selectedDate,
  onDateSelect,
  onBack,
  serviceName,
  durationMinutes,
  specialistName,
}: {
  month: Date;
  onMonthChange: (d: Date) => void;
  calendarDays: { date: string; available: boolean }[];
  calendarLoading: boolean;
  selectedDate: string;
  // Seleccionar un día avanza automáticamente al paso "time" (lo decide quien
  // consuma este componente). Cambiar de mes con las flechas NO dispara esto,
  // solo navega el calendario.
  onDateSelect: (d: string) => void;
  onBack: () => void;
  serviceName: string;
  durationMinutes: number;
  specialistName?: string | null;
}) {
  const dayMap = useMemo(() => {
    const m = new Map<string, boolean>();
    calendarDays.forEach((d) => m.set(d.date, d.available));
    return m;
  }, [calendarDays]);

  const monthDays = useMemo(() => {
    const start = startOfMonth(month);
    const end = endOfMonth(month);
    return eachDayOfInterval({ start, end });
  }, [month]);

  const today = startOfDay(new Date());
  const maxDate = addDays(today, 60);

  return (
    <div className="space-y-4">
      <button
        onClick={onBack}
        className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
      >
        <ChevronLeft className="w-4 h-4" /> Volver
      </button>

      <div className="bg-white border rounded-xl p-3 text-sm text-gray-600">
        <div className="font-medium text-gray-900">{serviceName}</div>
        <div className="text-xs text-gray-500 mt-1 flex items-center gap-3">
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {durationMinutes} min
          </span>
          {specialistName && (
            <span className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              {specialistName}
            </span>
          )}
        </div>
      </div>

      <h2 className="text-base font-semibold text-gray-800">
        ¿Qué día te viene bien?
      </h2>

      {/* Calendario */}
      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => onMonthChange(subMonths(month, 1))}
            className="p-1.5 hover:bg-gray-100 rounded-lg"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="font-medium capitalize text-gray-800">
            {format(month, "MMMM yyyy", { locale: es })}
          </span>
          <button
            onClick={() => onMonthChange(addMonths(month, 1))}
            className="p-1.5 hover:bg-gray-100 rounded-lg"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-gray-500 mb-1">
          {["L", "M", "X", "J", "V", "S", "D"].map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>
        {calendarLoading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {/* offset según el primer día del mes (lunes=0) */}
            {Array.from({
              length: (getDay(monthDays[0]) + 6) % 7,
            }).map((_, i) => (
              <div key={`pad-${i}`} />
            ))}
            {monthDays.map((d) => {
              const ds = format(d, "yyyy-MM-dd");
              const isPast = isBefore(d, today);
              const tooFar = isBefore(maxDate, d);
              const available = dayMap.get(ds) ?? false;
              const disabled = isPast || tooFar || !available;
              const selected = ds === selectedDate;
              return (
                <button
                  key={ds}
                  disabled={disabled}
                  onClick={() => onDateSelect(ds)}
                  className={`aspect-square text-sm rounded-lg transition ${
                    selected
                      ? "bg-indigo-600 text-white font-semibold"
                      : disabled
                        ? "text-gray-300 cursor-not-allowed"
                        : "hover:bg-indigo-50 text-gray-800"
                  } ${
                    !disabled && !selected && available
                      ? "ring-1 ring-indigo-100"
                      : ""
                  }`}
                >
                  {format(d, "d")}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function TimeStep({
  date,
  slots,
  slotsLoading,
  selectedTime,
  onSelectSlot,
  onBack,
  onChangeDay,
  serviceName,
  durationMinutes,
  specialistName,
}: {
  date: string;
  slots: Slot[];
  slotsLoading: boolean;
  selectedTime: string;
  // Seleccionar una hora avanza automáticamente al siguiente paso (lo decide
  // quien consuma este componente).
  onSelectSlot: (s: Slot) => void;
  onBack: () => void;
  onChangeDay: () => void;
  serviceName: string;
  durationMinutes: number;
  specialistName?: string | null;
}) {
  return (
    <div className="space-y-4">
      <button
        onClick={onBack}
        className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
      >
        <ChevronLeft className="w-4 h-4" /> Volver
      </button>

      <div className="bg-white border rounded-xl p-3 text-sm text-gray-600">
        <div className="font-medium text-gray-900">{serviceName}</div>
        <div className="text-xs text-gray-500 mt-1 flex items-center gap-3">
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {durationMinutes} min
          </span>
          {specialistName && (
            <span className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              {specialistName}
            </span>
          )}
        </div>
      </div>

      <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
        <CalIcon className="w-4 h-4 text-indigo-600" />
        {format(new Date(date), "EEEE d 'de' MMMM", { locale: es })}
      </h2>

      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        {slotsLoading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
          </div>
        ) : slots.length === 0 ? (
          <div className="py-4 text-center space-y-3">
            <p className="text-sm text-gray-500">
              No hay horarios disponibles este día.
            </p>
            <button
              onClick={onChangeDay}
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium underline underline-offset-2"
            >
              Elegir otro día
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {slots.map((s) => (
              <button
                key={s.time}
                onClick={() => onSelectSlot(s)}
                className={`py-2 rounded-lg text-sm font-medium transition ${
                  selectedTime === s.time
                    ? "bg-indigo-600 text-white"
                    : "bg-indigo-50 hover:bg-indigo-100 text-indigo-700"
                }`}
              >
                {s.time}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
